import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import httpclient from "../../Utils";
import { formatDateWithOrdinal } from "../../Utils/DateUtils";

const OrderAuditTrialModal = ({ open, handleClose, jobId }) => {
  const [logsResponse, setLogsResponse] = useState(null); // Store full API response
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // DataGrid uses 0-indexed pages
  const [pageSize, setPageSize] = useState(10); // Default to 10 as per API response
  const [isLoadingPagination, setIsLoadingPagination] = useState(false); // Loading state for pagination

  useEffect(() => {
    const fetchOrderLogs = async (currentPage = 1, currentRowsPerPage = 10) => {
      // Only show top-level loader for initial load, not for pagination
      const isInitialLoad = logsResponse === null;
      if (isInitialLoad) {
        setLoading(true);
      }

      const API_URL = `orders/${jobId}/logs?page=${currentPage}&pagination=${currentRowsPerPage}`;

      try {
        const res = await httpclient.get(API_URL);

        const { data } = res;

        if (data.status === 200) {
          setLogsResponse(data.data);
        } else {
          console.log("error", data.msg);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        }
        setIsLoadingPagination(false);
      }
    };

    // Convert 0-indexed page to 1-indexed for API call
    fetchOrderLogs(page + 1, pageSize);
  }, [jobId, page, pageSize]);

  const handlePaginationModelChange = (newModel) => {
    // Check if page changed
    if (newModel.page !== page) {
      setPage(newModel.page);
      setIsLoadingPagination(true);
    }
    // Check if pageSize changed
    if (newModel.pageSize !== pageSize) {
      setPageSize(newModel.pageSize);
      setPage(0); // Reset to first page when changing page size
      setIsLoadingPagination(true);
    }
  };

  const paginatedLogs = useMemo(() => {
    // Use the logs from the server response and add id property for DataGrid
    return (logsResponse?.data || []).map((log, index) => ({
      ...log,
      id: log.id || index,
      formattedDate: formatDateWithOrdinal(log.actionDate),
    }));
  }, [logsResponse]);

  const columns = [
    {
      field: "formattedDate",
      headerName: "Time",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "actionMessage",
      headerName: "Log",
      flex: 2,
      minWidth: 300,
    },
    {
      field: "actionBy",
      headerName: "Action By",
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 1000,
        },
      }}
    >
      <DialogContent sx={{ p: 4, pb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Lexend",
            fontSize: "20px",
            mb: 3,
            fontWeight: 500,
            color: "383E49",
          }}
        >
          Order Audit Trail
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "left",
            display: "inline-block",
            marginBottom: "16px",
          }}
        >
          Job No:{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{ mt: 2, textAlign: "left" }}
          >
            <strong>{jobId}</strong>
          </Typography>
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 450,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 450, width: "100%" }}>
            <DataGrid
              rows={paginatedLogs || []}
              columns={columns}
              rowCount={logsResponse?.total || 0}
              loading={isLoadingPagination}
              pagination
              paginationModel={{ page, pageSize }}
              paginationMode="server"
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[5, 10, 20, 50]}
              disableRowSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  fontSize: "14px",
                  fontWeight: 500,
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#d6d6d6ff !important",
                  color: "#fff !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  color: "#242424ff !important",
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: "rgba(177, 177, 177, 1)",
                },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ mr: 1, borderRadius: 1 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderAuditTrialModal;
